import { useCallback, useEffect, useRef, useState } from 'react';
import { Headphones, Square } from 'lucide-react';
import { Room, RoomEvent, Track, type RemoteTrackPublication, type RemoteParticipant } from 'livekit-client';
import { fetchGuestChannels, fetchGuestListenToken } from './guest-session';

/** A mission channel a guest may listen to. */
interface GuestChannel {
  id: number;
  name: string;
}

/**
 * Listen-only Echo audio for an audio-granted guest. Lists the mission channels
 * and connects to one at a time as a passive subscriber — the brokered token is
 * subscribe-only, so a guest can hear but never transmit. No mic, no publish.
 */
export function GuestAudio({ token }: { token: string }): React.JSX.Element | null {
  const [channels, setChannels] = useState<GuestChannel[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const roomRef = useRef<Room | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchGuestChannels(token).then((list) => {
      if (!cancelled) setChannels(list);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const stop = useCallback((): void => {
    roomRef.current?.disconnect();
    roomRef.current = null;
    document.querySelectorAll('[id^=guest-audio-]').forEach((el) => el.remove());
    setActiveId(null);
  }, []);

  useEffect(() => stop, [stop]);

  const listen = useCallback(
    async (channel: GuestChannel): Promise<void> => {
      stop();
      setError(null);
      const creds = await fetchGuestListenToken(token, channel.id);
      if (!creds) {
        setError('Could not join audio');
        return;
      }
      const room = new Room({ adaptiveStream: true, dynacast: false });
      roomRef.current = room;
      room.on(
        RoomEvent.TrackSubscribed,
        (track: Track, _pub: RemoteTrackPublication, _p: RemoteParticipant): void => {
          if (track.kind !== Track.Kind.Audio) return;
          const el = track.attach();
          el.id = `guest-audio-${track.sid}`;
          document.body.appendChild(el);
        },
      );
      room.on(RoomEvent.TrackUnsubscribed, (track: Track): void => {
        track.detach().forEach((el) => el.remove());
      });
      room.on(RoomEvent.Disconnected, () => setActiveId(null));
      try {
        await room.connect(creds.wsUrl, creds.token, { autoSubscribe: true });
        setActiveId(channel.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connection failed');
      }
    },
    [stop, token],
  );

  if (channels.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-echo-border bg-black/30 p-3 text-left">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Headphones className="h-3.5 w-3.5" /> Listen in
      </p>
      <div className="flex flex-col gap-1.5">
        {channels.map((channel) => {
          const active = activeId === channel.id;
          return (
            <button
              key={channel.id}
              onClick={() => (active ? stop() : void listen(channel))}
              className="flex items-center justify-between rounded border border-zinc-700/40 bg-zinc-800/40 px-2.5 py-1.5 text-xs text-foreground transition-colors hover:border-echo-yellow/40"
            >
              <span className="truncate">{channel.name}</span>
              {active ? (
                <span className="flex items-center gap-1 text-[10px] font-semibold text-red-400">
                  <Square className="h-2.5 w-2.5" /> Stop
                </span>
              ) : (
                <span className="text-[10px] text-muted-foreground">Listen</span>
              )}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-2 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
